// Favorite faces, as the face maker holds them (SCENARIOS L2,
// PLAN-2026-10-24-faces-round-three §1, `GET /api/me/faces`).
//
// READ AS THE MAKER OPENS, and held while it is open: the maker is the only
// screen that shows them, and a favorite saved on another device shows the
// next time it opens. Each save and removal answers the whole list, which
// replaces what was held.
//
// THE NEWEST ANSWER WINS. Every save and removal answers the whole list, so
// an answer to anything sent before the last of them is older than it and is
// dropped, a read's included; a write that fails reads the list again.
//
// NOBODY SIGNED IN HAS NONE: favorites are kept on an account, and the tour's
// visitor has none to keep them on (`signedIn` false), which the maker says.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FACE_FAVORITES_MAX, Face, FaceFavoritesView, type FaceFavorite } from '@fsp/domain';
import { tell } from '../kit/ui';
import { get, messageOf, send } from './api';
import { useSession } from './session';

export interface FaceFavoritesRead {
  /** Whether there is an account to keep favorites on. */
  signedIn: boolean;
  /** Newest first; null until read, and for nobody signed in. */
  faces: readonly FaceFavorite[] | null;
  /** The achievements this account holds that earn a part (§5). */
  earned: readonly string[];
  max: number;
  /** Why the list could not be read, while there is none to show. */
  failure: string | null;
  /** The favorite this face is, when it is one. */
  find: (face: Face) => FaceFavorite | undefined;
  /** Keep a face; answers whether it was kept (a refusal is already told). */
  save: (face: Face) => Promise<boolean>;
  /** Take one out; answers whether it went. */
  remove: (id: string) => Promise<boolean>;
  retry: () => void;
}

/** A face as the schema writes it: one spelling for one face, as the store keeps it. */
const spelling = (face: Face): string => JSON.stringify(Face.parse(face));

export function useFaceFavorites(): FaceFavoritesRead {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const [view, setView] = useState<FaceFavoritesView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [asked, setAsked] = useState(0);
  /** How many writes have been sent, and the last whose answer was taken. */
  const sent = useRef(0);
  const taken = useRef(0);
  /** Take a write's answer, unless a later write's has been taken. */
  const take = useCallback((n: number, next: FaceFavoritesView) => {
    if (n < taken.current) return;
    taken.current = n;
    setView(next);
    setFailure(null);
  }, []);

  useEffect(() => {
    setView(null);
    setFailure(null);
    if (userId === null) return;
    let live = true;
    const before = sent.current;
    void get('/me/faces', FaceFavoritesView).then((reply) => {
      // A write sent since answers a newer list than this.
      if (!live || sent.current !== before) return;
      if (reply.status === 'ok') setView(reply.data);
      else setFailure(messageOf(reply, 'Your favorites could not be read.'));
    });
    return () => {
      live = false;
    };
  }, [userId, asked]);

  const save = useCallback(
    async (face: Face) => {
      const n = ++sent.current;
      const reply = await send('POST', '/me/faces', { face }, FaceFavoritesView);
      if (reply.status !== 'ok') {
        setAsked((a) => a + 1);
        tell(
          messageOf(
            reply,
            'That face could not be kept.',
            'Could not reach the site. Nothing was saved.',
          ),
          'bad',
        );
        return false;
      }
      take(n, reply.data);
      return true;
    },
    [take],
  );

  const remove = useCallback(
    async (id: string) => {
      const n = ++sent.current;
      const reply = await send(
        'DELETE',
        `/me/faces/${encodeURIComponent(id)}`,
        undefined,
        FaceFavoritesView,
      );
      if (reply.status !== 'ok') {
        setAsked((a) => a + 1);
        tell(messageOf(reply, 'That favorite could not be taken out.'), 'bad');
        return false;
      }
      take(n, reply.data);
      return true;
    },
    [take],
  );

  const faces = view?.faces ?? null;
  // EACH FAVORITE BY ITS ONE SPELLING (`Face.parse`, the schema's key order),
  // so asking whether the face on screen is one is a lookup and not a parse
  // of every favorite on every change.
  const byFace = useMemo(
    () => new Map((faces ?? []).map((f) => [spelling(f.face), f] as const)),
    [faces],
  );
  return {
    signedIn: userId !== null,
    faces,
    earned: view?.earned ?? [],
    max: view?.max ?? FACE_FAVORITES_MAX,
    failure: faces === null ? failure : null,
    find: (face) => byFace.get(spelling(face)),
    save,
    remove,
    retry: () => setAsked((n) => n + 1),
  };
}
